import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, PackageX, Download, Settings, Send } from "lucide-react";
import { toast } from "sonner";
import { ArticoliGrouped } from "@/components/ArticoliGrouped";

const ROLE_LABELS = { admin: "Admin", commercial_manager: "Responsabile Commerciale", back_office: "Back Office", head_of_partner: "Head of Partner" };

export default function OrdinatiScoperti() {
  const [data, setData] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [emailsText, setEmailsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/magazzino/ordinati-scoperti`).then((r) => setData(r.data)).catch(() => setData(false));
    api.get(`/report-scoperti/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/magazzino/ordinati-scoperti/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = "ordinati-scoperti-torri.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download PDF non riuscito"); } finally { setPdfBusy(false); }
  };

  const openCfg = () => {
    setEmailsText((cfg?.recipients?.emails || []).join(", "));
    setCfgOpen(true);
  };
  const toggleRole = (role) => setCfg((c) => {
    const has = c.recipients.roles.includes(role);
    const roles = has ? c.recipients.roles.filter((r) => r !== role) : [...c.recipients.roles, role];
    return { ...c, recipients: { ...c.recipients, roles } };
  });
  const saveCfg = async () => {
    setSaving(true);
    try {
      const emails = emailsText.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
      const body = { enabled: cfg.enabled, subject: cfg.subject, intro: cfg.intro, send_time: cfg.send_time, recipients: { roles: cfg.recipients.roles, emails } };
      const r = await api.put(`/report-scoperti/config`, body);
      setCfg(r.data); toast.success("Configurazione salvata"); setCfgOpen(false);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Non hai i permessi per modificare l'invio" : "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };
  const inviaOra = async () => {
    setSending(true);
    try { await api.post(`/report-scoperti/invia-ora`); toast.success("Report inviato ora ai destinatari"); }
    catch (e) { toast.error(e?.response?.status === 403 ? "Non hai i permessi" : "Invio non riuscito — controlla i destinatari nella configurazione"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="ordinati-scoperti-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Articoli scoperti (commesse in corso)</h1>
          <p className="text-sm text-muted-foreground">Articoli già ordinati (commesse in corso) che non hanno copertura a magazzino.</p>
          {cfg && (
            <Badge className={`mt-2 ${cfg.enabled ? "bg-green-600 hover:bg-green-600" : "bg-muted text-muted-foreground hover:bg-muted"}`} data-testid="scoperti-invio-state">
              {cfg.enabled ? `Invio automatico ON · ogni giorno alle ${cfg.send_time}` : "Invio automatico OFF"}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={openCfg} data-testid="scoperti-config-btn">
            <Settings className="mr-1.5 h-4 w-4" /> Configura invio
          </Button>
          <Button variant="outline" size="sm" onClick={inviaOra} disabled={sending} data-testid="scoperti-invia-ora">
            {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Invia ora
          </Button>
          {data?.articoli?.length ? (
            <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy} data-testid="ord-pdf-btn">
              {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Scarica PDF
            </Button>
          ) : null}
        </div>
      </div>

      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="scoperti-config-dialog">
          <DialogHeader><DialogTitle>Configura invio — Articoli scoperti</DialogTitle></DialogHeader>
          {!cfg ? (
            <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              <div className={`flex items-center justify-between rounded-lg border p-3 ${cfg.enabled ? "border-green-600/40 bg-green-600/5" : "border-amber-500/40 bg-amber-500/5"}`}>
                <div>
                  <div className="text-sm font-semibold">Invio automatico giornaliero</div>
                  <div className="text-xs text-muted-foreground">{cfg.enabled ? "Attivo: il report parte ogni giorno all'orario impostato." : "Spento: nessun invio automatico (puoi comunque usare 'Invia ora')."}</div>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <span className={cfg.enabled ? "font-semibold text-green-600" : "font-semibold text-amber-600"}>{cfg.enabled ? "ON" : "OFF"}</span>
                  <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="scoperti-enabled" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Orario invio</Label><Input type="time" value={cfg.send_time} onChange={(e) => setCfg((c) => ({ ...c, send_time: e.target.value }))} data-testid="scoperti-send-time" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Oggetto</Label><Input value={cfg.subject} onChange={(e) => setCfg((c) => ({ ...c, subject: e.target.value }))} data-testid="scoperti-subject" /></div>
              <div className="space-y-1"><Label className="text-xs">Testo introduttivo</Label><Textarea rows={4} value={cfg.intro} onChange={(e) => setCfg((c) => ({ ...c, intro: e.target.value }))} className="text-xs" data-testid="scoperti-intro" /></div>
              <div className="space-y-2">
                <Label className="text-xs">Destinatari — ruoli</Label>
                <div className="flex flex-wrap gap-2">
                  {(cfg.roles_disponibili || []).map((role) => {
                    const on = cfg.recipients.roles.includes(role);
                    return (
                      <button key={role} type="button" onClick={() => toggleRole(role)} data-testid={`scoperti-role-${role}`}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-muted/40 text-muted-foreground"}`}>
                        {ROLE_LABELS[role] || role}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email extra (separate da virgola)</Label>
                <Textarea rows={2} value={emailsText} onChange={(e) => setEmailsText(e.target.value)} placeholder="mario@azienda.it, ufficio@azienda.it" className="text-xs" data-testid="scoperti-emails" />
              </div>
              <p className="text-[11px] text-muted-foreground">Il report viene inviato in testo semplice con il PDF completo in allegato. Se non selezioni nessun destinatario, l'invio automatico non parte.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCfgOpen(false)}>Annulla</Button>
            <Button onClick={saveCfg} disabled={saving || !cfg} data-testid="scoperti-config-save">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-5" data-testid="mag-ordinati-scoperti">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight">
          <PackageX className="h-4 w-4 text-primary" /> Articoli ordinati senza copertura
          {data?.articoli?.length ? <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">{data.articoli.length}</span> : null}
        </h2>
        <p className="mb-4 mt-1 text-xs text-muted-foreground">
          Calcolato dai <b>movimenti</b> di magazzino: unisce i <b>residui</b> delle commesse in corso e gli <b>ordini senza commessa inclusi</b> dall'operatore. <b className="text-red-600 dark:text-red-400">Da comprare</b> = impegni mai coperti da giacenza + arrivi; <b className="text-amber-600 dark:text-amber-400">in ritardo</b> = coperti ma da un arrivo posteriore alla data. Divisi per categoria. L'icona ℹ mostra ingressi/uscite; il N° apre le fonti.
          {data?.tot_scoperto != null && <> · Da comprare: <b>{data.tot_scoperto}</b> pz · In ritardo: <b>{data.tot_ritardo}</b> pz · Fonti: {data.n_fonti} (commesse in corso: {data.n_commesse_in_corso}).</>}
        </p>
        {data === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Calcolo in corso…</div>
        ) : !data?.articoli?.length ? (
          <p className="text-sm text-muted-foreground" data-testid="mag-ordinati-empty">Nessun articolo ordinato scoperto.</p>
        ) : (
          <ArticoliGrouped articoli={data.articoli} countKey="n_commesse" countHeader="N° fonti" />
        )}
      </Card>
    </div>
  );
}
